import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { getHuifuAccountList, huifuToolsQueryBalance } from '../../api';
import { exportToCSV } from '../../utils/exportUtils';

const { Text } = Typography;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const toMoney = (value) => {
  const n = Number.parseFloat(String(value ?? '').trim());
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (value) => `¥${toMoney(value).toFixed(2)}`;

const INTERVAL_OPTIONS = [
  { value: 800, label: '0.8 秒/户' },
  { value: 1000, label: '1 秒/户' },
  { value: 1500, label: '1.5 秒/户' },
  { value: 2000, label: '2 秒/户' },
];

const FILTER_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'ok', label: '仅成功' },
  { value: 'fail', label: '仅失败' },
];

const CSV_COLUMN_MAP = {
  realName: '房东姓名',
  phone: '手机号',
  userId: '用户ID',
  huifuId: '汇付号',
  statusText: '账号状态',
  resultText: '查询结果',
  avlBal: '可用余额',
  balAmt: '账户余额',
  frzBal: '冻结余额',
  error: '错误信息',
};

/** 分页拉齐汇付账号，按 huifu_id 去重（优先 status=1、较新 id） */
async function loadDistinctLandlords() {
  const pageSize = 100;
  let page = 1;
  let total = Infinity;
  const byHuifu = new Map();

  while ((page - 1) * pageSize < total) {
    const res = await getHuifuAccountList('', page, pageSize);
    if (!res?.success && res?.code !== 0) {
      throw new Error(res?.msg || '拉取汇付账号列表失败');
    }
    const list = Array.isArray(res?.data) ? res.data : [];
    total = Number(res?.total ?? list.length);
    for (const row of list) {
      const huifuId = String(row?.huifu_id || '').trim();
      if (!huifuId) continue;
      const prev = byHuifu.get(huifuId);
      const next = {
        huifuId,
        userId: row.user_id,
        realName: row.real_name || '',
        phone: row.phone || '',
        status: Number(row.status),
        localId: row.id,
      };
      if (!prev) {
        byHuifu.set(huifuId, next);
        continue;
      }
      const prevBetter = Number(prev.status) === 1 && Number(next.status) !== 1;
      const nextBetter = Number(next.status) === 1 && Number(prev.status) !== 1;
      if (nextBetter || (!prevBetter && Number(next.localId || 0) > Number(prev.localId || 0))) {
        byHuifu.set(huifuId, next);
      }
    }
    if (list.length < pageSize) break;
    page += 1;
    if (page > 200) break;
  }

  return Array.from(byHuifu.values()).sort((a, b) => Number(a.userId || 0) - Number(b.userId || 0));
}

function parseBalanceRes(res, landlord) {
  const payload = res?.data || {};
  const summary = payload.summary || {};
  const callError = payload.call_error || '';
  const respCode = String(summary.resp_code || payload.response?.resp_code || '');
  const respDesc = String(summary.resp_desc || payload.response?.resp_desc || '');
  const avl = toMoney(summary.total_avl_bal);
  const bal = toMoney(summary.total_bal);
  const frz = toMoney(summary.total_frz_bal);
  const ok = !callError && (respCode === '' || respCode === '00000000' || respCode === '10000');
  return {
    key: landlord.huifuId,
    userId: landlord.userId,
    realName: landlord.realName,
    phone: landlord.phone,
    status: landlord.status,
    huifuId: landlord.huifuId,
    avlBal: avl,
    balAmt: bal,
    frzBal: frz,
    accountCount: summary.account_count ?? (Array.isArray(payload.accounts) ? payload.accounts.length : 0),
    ok,
    error: callError || (ok ? '' : `${respCode} ${respDesc}`.trim()),
    respCode,
    respDesc,
  };
}

function failRowFromError(landlord, err) {
  return {
    key: landlord.huifuId,
    userId: landlord.userId,
    realName: landlord.realName,
    phone: landlord.phone,
    status: landlord.status,
    huifuId: landlord.huifuId,
    avlBal: 0,
    balAmt: 0,
    frzBal: 0,
    accountCount: 0,
    ok: false,
    error: err?.response?.data?.msg || err?.message || '请求失败',
    respCode: '',
    respDesc: '',
  };
}

function isRateLimitError(err, parsed) {
  const text = [
    err?.message,
    err?.response?.data?.msg,
    parsed?.error,
    parsed?.respDesc,
    parsed?.respCode,
  ]
    .filter(Boolean)
    .join(' ');
  return /限流|频率|too many|rate.?limit|429|过快/i.test(text) || err?.response?.status === 429;
}

async function queryOneBalance(landlord, intervalMs, allowRetry) {
  try {
    let res = await huifuToolsQueryBalance(landlord.huifuId);
    let parsed = parseBalanceRes(res, landlord);
    if (allowRetry && isRateLimitError(null, parsed)) {
      await sleep(Math.max(intervalMs * 2, 3000));
      res = await huifuToolsQueryBalance(landlord.huifuId);
      parsed = parseBalanceRes(res, landlord);
    }
    return parsed;
  } catch (err) {
    let parsed = failRowFromError(landlord, err);
    if (allowRetry && isRateLimitError(err, parsed)) {
      await sleep(Math.max(intervalMs * 2, 3000));
      try {
        const res = await huifuToolsQueryBalance(landlord.huifuId);
        parsed = parseBalanceRes(res, landlord);
      } catch (err2) {
        parsed = failRowFromError(landlord, err2);
      }
    }
    return parsed;
  }
}

export default function LandlordBalanceBatch() {
  const [intervalMs, setIntervalMs] = useState(1000);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [progress, setProgress] = useState({ current: 0, total: 0, currentName: '' });
  const [rows, setRows] = useState([]);
  const [resultFilter, setResultFilter] = useState('all');
  const [retryingKey, setRetryingKey] = useState('');
  const abortRef = useRef(false);

  const stats = useMemo(() => {
    const okRows = rows.filter((r) => r.ok);
    const failRows = rows.filter((r) => !r.ok);
    return {
      total: rows.length,
      ok: okRows.length,
      fail: failRows.length,
      totalAvl: okRows.reduce((s, r) => s + r.avlBal, 0),
      totalBal: okRows.reduce((s, r) => s + r.balAmt, 0),
      totalFrz: okRows.reduce((s, r) => s + r.frzBal, 0),
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (resultFilter === 'ok') return rows.filter((r) => r.ok);
    if (resultFilter === 'fail') return rows.filter((r) => !r.ok);
    return rows;
  }, [rows, resultFilter]);

  const stop = useCallback(() => {
    abortRef.current = true;
  }, []);

  const runBatch = useCallback(async () => {
    abortRef.current = false;
    setRunning(true);
    setRows([]);
    setResultFilter('all');
    setProgress({ current: 0, total: 0, currentName: '' });
    setPhase('loading_list');

    let landlords = [];
    try {
      landlords = await loadDistinctLandlords();
    } catch (err) {
      message.error(err.message || '拉取房东列表失败');
      setRunning(false);
      setPhase('idle');
      return;
    }

    if (!landlords.length) {
      message.warning('未找到有效 huifu_id 的房东');
      setRunning(false);
      setPhase('idle');
      return;
    }

    const etaMin = ((landlords.length * intervalMs) / 60000).toFixed(1);
    message.info(`共 ${landlords.length} 个汇付号，约需 ${etaMin} 分钟（勿关页面）`);
    setPhase('querying');
    setProgress({ current: 0, total: landlords.length, currentName: '' });

    const nextRows = [];
    for (let i = 0; i < landlords.length; i += 1) {
      if (abortRef.current) {
        setPhase('stopped');
        setRows([...nextRows]);
        setRunning(false);
        message.warning(`已停止：完成 ${nextRows.length}/${landlords.length}`);
        return;
      }

      const landlord = landlords[i];
      setProgress({
        current: i + 1,
        total: landlords.length,
        currentName: landlord.realName || landlord.phone || landlord.huifuId,
      });

      const parsed = await queryOneBalance(landlord, intervalMs, true);
      nextRows.push(parsed);
      setRows([...nextRows]);

      if (i < landlords.length - 1) {
        await sleep(intervalMs);
      }
    }

    setPhase(abortRef.current ? 'stopped' : 'done');
    setRunning(false);
    if (!abortRef.current) {
      message.success('全部查询完成');
    }
  }, [intervalMs]);

  const retryOne = useCallback(
    async (record) => {
      if (running || retryingKey) return;
      const landlord = {
        huifuId: record.huifuId,
        userId: record.userId,
        realName: record.realName,
        phone: record.phone,
        status: record.status,
      };
      setRetryingKey(record.key);
      try {
        const parsed = await queryOneBalance(landlord, intervalMs, true);
        setRows((prev) => prev.map((r) => (r.key === record.key ? parsed : r)));
        if (parsed.ok) message.success('重查成功');
        else message.error(parsed.error || '重查失败');
      } catch (err) {
        message.error(err.message || '重查失败');
      } finally {
        setRetryingKey('');
      }
    },
    [intervalMs, retryingKey, running],
  );

  const exportCsv = useCallback(() => {
    const source = filteredRows.length ? filteredRows : rows;
    if (!source.length) {
      message.warning('暂无数据可导出');
      return;
    }
    const data = source.map((r) => ({
      realName: r.realName || '',
      phone: r.phone || '',
      userId: r.userId ?? '',
      huifuId: r.huifuId || '',
      statusText: Number(r.status) === 1 ? '有效' : '其他',
      resultText: r.ok ? '成功' : '失败',
      avlBal: r.ok ? Number(r.avlBal || 0).toFixed(2) : '',
      balAmt: r.ok ? Number(r.balAmt || 0).toFixed(2) : '',
      frzBal: r.ok ? Number(r.frzBal || 0).toFixed(2) : '',
      error: r.ok ? '' : r.error || '',
    }));
    const suffix = resultFilter === 'fail' ? '失败' : resultFilter === 'ok' ? '成功' : '全部';
    const ok = exportToCSV(data, `房东汇付余额_${suffix}`, CSV_COLUMN_MAP);
    if (ok) message.success('CSV 已导出');
    else message.error('导出失败');
  }, [filteredRows, resultFilter, rows]);

  const columns = [
    {
      title: '房东',
      width: 160,
      render: (_, r) => (
        <div>
          <div>{r.realName || '-'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.phone || `ID: ${r.userId || '-'}`}
          </Text>
        </div>
      ),
    },
    { title: '用户ID', dataIndex: 'userId', width: 90 },
    { title: '汇付号', dataIndex: 'huifuId', width: 170, ellipsis: true },
    {
      title: '状态',
      dataIndex: 'status',
      width: 70,
      render: (v) => (Number(v) === 1 ? <Tag color="success">有效</Tag> : <Tag>其他</Tag>),
    },
    {
      title: '可用余额',
      dataIndex: 'avlBal',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.avlBal - b.avlBal,
      render: (v, r) => (r.ok ? <Text strong type="success">{formatMoney(v)}</Text> : '-'),
    },
    {
      title: '账户余额',
      dataIndex: 'balAmt',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.balAmt - b.balAmt,
      render: (v, r) => (r.ok ? formatMoney(v) : '-'),
    },
    {
      title: '冻结',
      dataIndex: 'frzBal',
      width: 100,
      align: 'right',
      render: (v, r) => (r.ok ? formatMoney(v) : '-'),
    },
    {
      title: '结果',
      width: 200,
      render: (_, r) =>
        r.ok ? <Tag color="success">成功</Tag> : <Tag color="error">{r.error || '失败'}</Tag>,
    },
    {
      title: '操作',
      width: 90,
      fixed: 'right',
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          disabled={running}
          loading={retryingKey === r.key}
          onClick={() => retryOne(r)}
        >
          重查
        </Button>
      ),
    },
  ];

  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="房东余额一键查询" />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <span>请求间隔</span>
          <Select
            style={{ width: 160 }}
            value={intervalMs}
            disabled={running}
            options={INTERVAL_OPTIONS}
            onChange={setIntervalMs}
          />
          <Button type="primary" loading={running && phase === 'loading_list'} disabled={running} onClick={runBatch}>
            一键查询全部房东余额
          </Button>
          <Button danger disabled={!running} onClick={stop}>
            停止
          </Button>
          <Button disabled={!rows.length || running} onClick={exportCsv}>
            导出 CSV
          </Button>
        </Space>
        {(running || phase === 'done' || phase === 'stopped') && progress.total > 0 && (
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={pct}
              status={phase === 'stopped' ? 'exception' : phase === 'done' ? 'success' : 'active'}
              format={() => `${progress.current}/${progress.total}`}
            />
            <Text type="secondary">
              {phase === 'loading_list'
                ? '正在拉取房东列表…'
                : phase === 'querying'
                  ? `正在查：${progress.currentName}`
                  : phase === 'stopped'
                    ? '已停止'
                    : '已完成'}
            </Text>
          </div>
        )}
      </Card>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card
            hoverable
            onClick={() => setResultFilter('all')}
            style={resultFilter === 'all' ? { borderColor: '#1677ff' } : undefined}
          >
            <Statistic title="已查户数" value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card
            hoverable
            onClick={() => setResultFilter('ok')}
            style={resultFilter === 'ok' ? { borderColor: '#3f8600' } : undefined}
          >
            <Statistic title="成功" value={stats.ok} valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card
            hoverable
            onClick={() => setResultFilter('fail')}
            style={resultFilter === 'fail' ? { borderColor: '#cf1322' } : undefined}
          >
            <Statistic title="失败" value={stats.fail} valueStyle={{ color: stats.fail ? '#cf1322' : undefined }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="可用余额合计" value={stats.totalAvl} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="账户余额合计" value={stats.totalBal} precision={2} prefix="¥" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="冻结合计" value={stats.totalFrz} precision={2} prefix="¥" />
          </Card>
        </Col>
      </Row>

      <Card
        title="明细"
        extra={
          <Space>
            <span>筛选</span>
            <Select
              style={{ width: 120 }}
              value={resultFilter}
              options={FILTER_OPTIONS}
              onChange={setResultFilter}
            />
            <Button size="small" disabled={!rows.length || running} onClick={exportCsv}>
              导出 CSV
            </Button>
          </Space>
        }
      >
        <Table
          rowKey="key"
          columns={columns}
          dataSource={filteredRows}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          rowClassName={(r) => (!r.ok ? 'landlord-balance-fail-row' : '')}
          locale={{ emptyText: resultFilter === 'fail' ? '暂无失败记录' : '暂无数据' }}
        />
      </Card>

      <style>{`
        .landlord-balance-fail-row > td {
          background: #fff2f0 !important;
        }
      `}</style>
    </div>
  );
}
